import { useMemo, useState } from 'react';
import {
  ALL_SHOPS,
  DEFAULT_ORDER_FILTER,
  DEFAULT_SORT_KEY,
  OrderFilterKey,
  OrderSortKey,
  ShopFilterKey,
} from '../utils/orderFilters';

export type OrderFilters = {
  orderFilter: OrderFilterKey;
  setOrderFilter: (key: OrderFilterKey) => void;
  sortKey: OrderSortKey;
  setSortKey: (key: OrderSortKey) => void;
  /**
   * Which restaurant's orders to show. Only the cross-restaurant search page offers a choice here;
   * the per-restaurant views are already one restaurant and leave it at `ALL_SHOPS`.
   */
  shopFilter: ShopFilterKey;
  setShopFilter: (key: ShopFilterKey) => void;
};

/**
 * Which orders the list shows and in what order.
 *
 * Lifted out of `OrdersList` so a page can place the filter bar somewhere of its own — the
 * restaurant view sets it beside the date strip — while the list keeps deciding what the two
 * settings mean. A page that has no opinion leaves it alone and the list holds its own.
 */
export const useOrderFilters = (): OrderFilters => {
  const [orderFilter, setOrderFilter] = useState<OrderFilterKey>(DEFAULT_ORDER_FILTER);
  const [sortKey, setSortKey] = useState<OrderSortKey>(DEFAULT_SORT_KEY);
  const [shopFilter, setShopFilter] = useState<ShopFilterKey>(ALL_SHOPS);

  // One object per actual change of setting, rather than a fresh one every render: this is passed
  // whole to `OrdersList`, and a new identity each time makes it useless as a memo dependency there.
  // The setters are `useState`'s own and never change, so they are not dependencies.
  return useMemo(
    () => ({ orderFilter, setOrderFilter, sortKey, setSortKey, shopFilter, setShopFilter }),
    [orderFilter, sortKey, shopFilter]
  );
};
